// ===================================================================
// src/app/lib/chatSocket.ts
//
// Cliente WebSocket do chat interno da equipe.
// Protocolo: ws(s)://<api-host>/ws/chat/<group_id>/?token=<access_token>
// (apps/chat/consumers.py + apps/chat/routing.py no eleveia-api — montado
// na raiz do host, FORA do prefixo /api/v1 usado pelas rotas REST).
//
// Estilo fábrica/closure (não classe) — mesma linha do resto de src/app/lib
// (api.ts exporta funções soltas, não uma classe de cliente HTTP).
//
// Limitação conhecida (v1): frames enviados enquanto o socket não está
// aberto são descartados com um console.warn — não há fila de reenvio.
// Quem chama `send()` deve ter um fallback (ex: ChatComposer usa REST
// quando `getReadyState() !== OPEN`).
// ===================================================================

import { getAccessToken } from './api';

// ── Tipos de frame ───────────────────────────────────────────────────────────

export type ChatClientFrame =
  | { type: 'message.send'; content: string; mentioned_user_ids?: number[] }
  | { type: 'message.edit'; message_id: string; content: string }
  | { type: 'message.delete'; message_id: string }
  | { type: 'reaction.toggle'; message_id: string; emoji: string }
  | { type: 'read'; message_id: string }
  | { type: 'typing' };

export interface ChatServerFrameMap {
  'chat.message.new': unknown;
  'chat.message.updated': unknown;
  'chat.message.deleted': unknown;
  'chat.reaction.toggled': { message_id: string; emoji: string; user_id: number; added: boolean };
  'chat.read.updated': { user_id: number; message_id: string; sequence: number };
  'chat.typing': { user_id: number; username: string };
  error: { message: string };
}

export type ChatServerEventType = keyof ChatServerFrameMap;

/** Eventos internos de ciclo de vida da conexão (não vêm do servidor). */
export type ChatConnectionEventType = 'open' | 'close' | 'reconnecting' | 'connection-lost';

type EventHandler<T = unknown> = (payload: T) => void;

// Close codes de rejeição permanente — não faz sentido tentar reconectar.
const PERMANENT_FAILURE_CODES = new Set([4001, 4003, 4004]);

// Backoff exponencial (1s, 2s, 4s, 8s, 16s, 30s-cap) em vez de um intervalo
// fixo — 5 tentativas de 2s (10s no total) esgotavam rápido demais em rede
// instável (Wi-Fi de escola, VPN, proxy), deixando o usuário "preso" numa
// conversa que parecia funcionar mas não recebia mais nada. Com backoff, a
// janela total de tentativas passa a ~61s antes de desistir.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 6;

function reconnectDelayFor(attempt: number): number {
  return Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1), RECONNECT_MAX_DELAY_MS);
}

function buildWsUrl(groupId: string, token: string): string {
  const rawBase = (import.meta.env.VITE_API_URL ?? 'http://localhost:8000').replace(/\/+$/, '');
  const wsProtocol = rawBase.startsWith('https') ? 'wss' : 'ws';
  const hostAndPort = rawBase.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${hostAndPort}/ws/chat/${groupId}/?token=${encodeURIComponent(token)}`;
}

/**
 * Cria um cliente de chat para um único grupo. Uma instância = uma conexão.
 * Chame `disconnect()` ao trocar de grupo ou desmontar o componente, e crie
 * uma nova instância (ou chame `connect()` de novo) para o próximo grupo.
 */
export function createChatSocket() {
  let ws: WebSocket | null = null;
  let currentGroupId: string | null = null;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let manuallyDisconnected = false;

  const handlers = new Map<string, Set<EventHandler<any>>>();

  function emit(type: string, payload: unknown) {
    handlers.get(type)?.forEach((h) => h(payload));
  }

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function openSocket(groupId: string) {
    const token = getAccessToken();
    if (!token) {
      emit('connection-lost', { reason: 'no-token' });
      return;
    }

    const socket = new WebSocket(buildWsUrl(groupId, token));

    socket.onopen = () => {
      reconnectAttempts = 0;
      emit('open', undefined);
    };

    socket.onmessage = (event) => {
      let data: { type?: string; payload?: unknown; message?: string };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!data.type) return;
      emit(data.type, 'payload' in data ? data.payload : data);
    };

    socket.onclose = (event) => {
      ws = null;
      emit('close', { code: event.code });

      if (manuallyDisconnected) return;

      if (PERMANENT_FAILURE_CODES.has(event.code)) {
        emit('connection-lost', { reason: 'rejected', code: event.code });
        return;
      }

      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        emit('connection-lost', { reason: 'max-attempts', code: event.code });
        return;
      }

      reconnectAttempts += 1;
      const delay = reconnectDelayFor(reconnectAttempts);
      emit('reconnecting', { attempt: reconnectAttempts, max: MAX_RECONNECT_ATTEMPTS, delayMs: delay });
      clearReconnectTimer();
      reconnectTimer = setTimeout(() => {
        if (!manuallyDisconnected && currentGroupId) openSocket(currentGroupId);
      }, delay);
    };

    socket.onerror = () => {
      // onclose é sempre disparado logo em seguida — a lógica de
      // reconexão/erro fica centralizada lá para não duplicar.
    };

    ws = socket;
  }

  return {
    /** Abre a conexão para `groupId`. Fecha qualquer conexão anterior primeiro. */
    connect(groupId: string) {
      this.disconnect();
      manuallyDisconnected = false;
      reconnectAttempts = 0;
      currentGroupId = groupId;
      openSocket(groupId);
    },

    /** Fecha a conexão de forma limpa — nenhuma reconexão é tentada depois. */
    disconnect() {
      manuallyDisconnected = true;
      clearReconnectTimer();
      currentGroupId = null;
      if (ws) {
        ws.onclose = null;
        ws.close(1000, 'client disconnect');
        ws = null;
      }
    },

    /** Envia um frame se o socket estiver aberto; caso contrário, descarta com warn. */
    send(frame: ChatClientFrame) {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.warn('[chatSocket] send() ignorado — socket não está aberto:', frame.type);
        return false;
      }
      ws.send(JSON.stringify(frame));
      return true;
    },

    /**
     * Assina um evento do servidor (`chat.message.new`, etc.) ou de ciclo de
     * vida (`open`/`reconnecting`/`connection-lost`). Sem overload (objeto
     * literal não suporta assinaturas múltiplas) — quem consome tipa o
     * payload no próprio handler quando precisa.
     */
    on(type: ChatServerEventType | ChatConnectionEventType, handler: EventHandler<any>) {
      if (!handlers.has(type)) handlers.set(type, new Set());
      handlers.get(type)!.add(handler);
    },

    off(type: string, handler: EventHandler<any>) {
      handlers.get(type)?.delete(handler);
    },

    isOpen(): boolean {
      return ws !== null && ws.readyState === WebSocket.OPEN;
    },
  };
}

export type ChatSocket = ReturnType<typeof createChatSocket>;
